// Fingerprint contract shared with adle-backend.
// FNV-1a 32-bit over UTF-8 bytes of canonical JSON. See
// aiGeneratedFingerprint.fixtures.json.

import { GOOGLE, LINKEDIN, META, TIKTOK } from "./adsPlatformIds.js";

export const AI_GENERATED_VERSION = 1;

const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;

const TEXT_SECTIONS = new Set([
  "titles",
  "headlines",
  "descriptions",
  "longHeadlines",
  "callOuts",
  "keywords",
  "keywordsNegative",
  "searchThemes",
  "callToActions",
]);

const FORM_SECTIONS = new Set(["instantForm", "leadForm"]);

const AUDIENCE_SECTIONS = new Set([
  "interests",
  "behaviors",
  "demographics",
  "memberBehaviors",
  "industries",
  "jobFunctions",
  "seniorities",
  "jobTitles",
  "companySizes",
  "companies",
  "degrees",
  "fieldsOfStudy",
  "schools",
  "skills",
]);

export const AI_GENERATED_SECTIONS_BY_PLATFORM = {
  [META]: {
    ad: [
      "titles",
      "headlines",
      "descriptions",
      "callToAction",
      "callToActions",
      "instantForm",
    ],
    adGroup: ["interests", "behaviors", "demographics", "ageRange", "genders"],
  },
  [TIKTOK]: {
    ad: ["titles", "callToAction", "callToActions"],
    adGroup: ["interests", "behaviors", "ageRange", "genders"],
  },
  [GOOGLE]: {
    campaign: ["callOuts", "siteLinks"],
    ad: ["titles", "descriptions"],
    adGroup: ["keywords", "keywordsNegative", "titles", "descriptions"],
    assetsGroup: [
      "titles",
      "longHeadlines",
      "descriptions",
      "headlines",
      "searchThemes",
    ],
  },
  [LINKEDIN]: {
    assetsGroup: [
      "titles",
      "headlines",
      "descriptions",
      "callToAction",
      "leadForm",
    ],
    campaign: [
      "interests",
      "jobTitles",
      "industries",
      "skills",
      "seniorities",
      "jobFunctions",
      "companySizes",
      "companies",
      "degrees",
      "fieldsOfStudy",
      "schools",
      "memberBehaviors",
      "ageRange",
      "genders",
    ],
  },
};

export const AI_GENERATED_SECTION_NAMES = new Set(
  Object.values(AI_GENERATED_SECTIONS_BY_PLATFORM).flatMap((levels) =>
    Object.values(levels).flat()
  )
);

/** Card-level chips: edad + género (Meta, TikTok, LinkedIn ad group / campaign). */
export const AI_GENERATED_DEMOGRAPHICS_BASIC_SECTIONS = ["ageRange", "genders"];

/** LinkedIn — atributos de audiencia (excluye demografía básica). */
export const AI_GENERATED_LINKEDIN_AUDIENCE_ATTRIBUTE_SECTIONS = [
  "industries",
  "jobFunctions",
  "seniorities",
  "jobTitles",
  "companySizes",
  "companies",
  "degrees",
  "fieldsOfStudy",
  "schools",
  "skills",
  "interests",
  "memberBehaviors",
];

export const isAIGeneratedSectionForEntity = ({
  platform,
  entityType,
  section,
}) => {
  if (typeof section !== "string" || !section) return false;
  const sections = AI_GENERATED_SECTIONS_BY_PLATFORM[platform]?.[entityType];
  return Array.isArray(sections) && sections.includes(section);
};

export const aiGeneratedSectionFromFieldPath = (fieldPath) => {
  if (typeof fieldPath !== "string" || !fieldPath.includes(".")) return null;
  const section = fieldPath.split(".").pop();
  if (!section || !AI_GENERATED_SECTION_NAMES.has(section)) return null;
  const basePath = fieldPath.slice(0, -(section.length + 1));
  if (!basePath) return null;
  return { basePath, section };
};

let reportAIGeneratedError = () => {};

export const setAIGeneratedErrorReporter = (reporter) => {
  reportAIGeneratedError = typeof reporter === "function" ? reporter : () => {};
};

export const reportAIGeneratedChipFailure = ({
  functionName,
  section,
  entity,
}) => {
  reportFailure({ functionName, section, entity });
};

const reportFailure = ({ functionName, section, entity }) => {
  const sections = entity?.aiGenerated?.sections;
  const sectionCount =
    sections && typeof sections === "object" ? Object.keys(sections).length : 0;
  try {
    reportAIGeneratedError({
      functionName,
      context: {
        section: typeof section === "string" ? section : undefined,
        sectionCount,
      },
    });
  } catch {
    // Reporting must not break the chip or the save.
  }
};

const normalizeText = (value) =>
  String(value ?? "")
    .normalize("NFC")
    .trim();

const canonicalize = (value) => {
  if (value === undefined || value === null) return "null";
  if (typeof value === "string" || typeof value === "number") {
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalize(item)).join(",")}]`;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort();
    return `{${keys
      .map((key) => `${JSON.stringify(key)}:${canonicalize(value[key])}`)
      .join(",")}}`;
  }
  return "null";
};

export const fnv1a32Hex = (text) => {
  const bytes = new TextEncoder().encode(String(text));
  let hash = FNV_OFFSET_BASIS;
  for (let index = 0; index < bytes.length; index += 1) {
    hash ^= bytes[index];
    hash = Math.imul(hash, FNV_PRIME);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

const asTextList = (value, { sort = true } = {}) => {
  if (value === undefined || value === null || value === "") return [];
  const source = Array.isArray(value) ? value : [value];
  const texts = [];
  source.forEach((item) => {
    let text = "";
    if (typeof item === "string" || typeof item === "number") {
      text = normalizeText(item);
    } else if (item && typeof item === "object") {
      if (typeof item.value === "string" || typeof item.value === "number") {
        text = normalizeText(item.value);
      } else if (typeof item.text === "string") {
        text = normalizeText(item.text);
      } else if (typeof item.keyword === "string") {
        text = normalizeText(item.keyword);
      }
    }
    if (text) texts.push(text);
  });
  if (sort) texts.sort();
  return texts;
};

const asIdList = (value) => {
  if (!Array.isArray(value)) return [];
  const ids = [];
  value.forEach((item) => {
    if (typeof item === "string" || typeof item === "number") {
      const id = String(item).trim();
      if (id) ids.push(id);
      return;
    }
    if (item && typeof item === "object" && item.id != null) {
      const id = String(item.id).trim();
      if (id) ids.push(id);
    }
  });
  ids.sort();
  return ids;
};

const asFormId = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const id = String(value.id || "").trim();
  if (!id) return null;
  return { id };
};

const asAgeRange = (value) => {
  if (!Array.isArray(value)) return [];
  const numbers = [];
  value.slice(0, 2).forEach((item) => {
    if (typeof item === "number" && Number.isFinite(item)) {
      numbers.push(item);
      return;
    }
    if (typeof item === "string" && item.trim() !== "") {
      const number = Number(item.trim());
      if (Number.isFinite(number)) numbers.push(number);
    }
  });
  return numbers;
};

const asGenders = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const genders = {};
  Object.keys(value)
    .sort()
    .forEach((key) => {
      genders[key] = Boolean(value[key]);
    });
  if (Object.keys(genders).length === 0) return null;
  return genders;
};

const asSiteLinks = (value) => {
  if (!Array.isArray(value)) return [];
  const links = [];
  value.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const linkText = normalizeText(item.linkText || item.text || "");
    const descriptions = asTextList(item.descriptions || [], { sort: false });
    const finalUrl = normalizeText(item.finalUrl || item.url || "");
    if (!linkText && descriptions.length === 0 && !finalUrl) return;
    links.push({ descriptions, finalUrl, linkText });
  });
  links.sort((left, right) => {
    const leftKey = canonicalize(left);
    const rightKey = canonicalize(right);
    if (leftKey === rightKey) return 0;
    return leftKey < rightKey ? -1 : 1;
  });
  return links;
};

const asCallToAction = (value) => {
  if (typeof value === "string") return normalizeText(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value && typeof value === "object") return value;
  return normalizeText(value);
};

export const isEmptyAIGeneratedValue = (normalized) => {
  if (normalized === null || normalized === undefined) return true;
  if (typeof normalized === "string") return normalized.length === 0;
  if (Array.isArray(normalized)) return normalized.length === 0;
  return false;
};

export const normalizeAIGeneratedValue = (value, section) => {
  if (TEXT_SECTIONS.has(section)) return asTextList(value);
  if (AUDIENCE_SECTIONS.has(section)) return asIdList(value);
  if (FORM_SECTIONS.has(section)) return asFormId(value);
  if (section === "callToAction") return asCallToAction(value);
  if (section === "ageRange") return asAgeRange(value);
  if (section === "genders") return asGenders(value);
  if (section === "siteLinks") return asSiteLinks(value);
  return value === undefined ? null : value;
};

export const getAIGeneratedFingerprint = (value, section) =>
  fnv1a32Hex(canonicalize(normalizeAIGeneratedValue(value, section)));

const getSectionValue = (entity, section) => {
  if (!entity || typeof entity !== "object" || !section) return undefined;
  if (Object.prototype.hasOwnProperty.call(entity, section)) {
    return entity[section];
  }
  if (!section.includes(".")) return undefined;
  return section.split(".").reduce((current, key) => current?.[key], entity);
};

export const getIsAIGeneratedSection = ({ entity, section, value } = {}) => {
  try {
    if (typeof section !== "string" || !section) return false;
    if (entity?.aiGenerated?.v !== AI_GENERATED_VERSION) return false;
    const stored = entity?.aiGenerated?.sections?.[section];
    if (typeof stored !== "string" || !/^[0-9a-f]{8}$/.test(stored)) {
      return false;
    }
    const current =
      value !== undefined ? value : getSectionValue(entity, section);
    return getAIGeneratedFingerprint(current, section) === stored;
  } catch {
    reportFailure({
      functionName: "getIsAIGeneratedSection",
      section,
      entity,
    });
    return false;
  }
};

const isStoredAIGeneratedFingerprint = (stored) =>
  typeof stored === "string" && /^[0-9a-f]{8}$/.test(stored);

/** Sections in the group that have an aiGenerated stamp on this entity. */
export const getStampedAIGeneratedSectionsInGroup = (entity, sections = []) => {
  if (!entity?.aiGenerated?.sections || !Array.isArray(sections)) return [];
  return sections.filter((section) =>
    isStoredAIGeneratedFingerprint(entity.aiGenerated.sections[section])
  );
};

/**
 * Card-level chip: show only while every stamped subsection in the group still
 * matches. Editing any stamped field hides the chip (not OR across the group).
 */
export const getIsAIGeneratedSectionGroup = ({
  entity,
  sections = [],
} = {}) => {
  const sectionList = Array.isArray(sections)
    ? sections.filter((section) => typeof section === "string" && section)
    : [];
  if (!entity || sectionList.length === 0) return false;
  const stamped = getStampedAIGeneratedSectionsInGroup(entity, sectionList);
  if (stamped.length === 0) return false;
  return stamped.every((section) =>
    getIsAIGeneratedSection({
      entity,
      section,
      value: entity[section],
    })
  );
};

const stampAIGeneratedSectionsUnsafe = ({
  entity,
  sections,
  jobID,
  now = Date.now(),
}) => {
  const source = entity && typeof entity === "object" ? entity : {};
  const currentAI =
    source.aiGenerated?.v === AI_GENERATED_VERSION ? source.aiGenerated : null;
  const nextSections = { ...(currentAI?.sections || {}) };
  let didChange = false;

  Object.keys(sections || {}).forEach((section) => {
    if (!AI_GENERATED_SECTION_NAMES.has(section)) return;
    const normalized = normalizeAIGeneratedValue(sections[section], section);
    if (isEmptyAIGeneratedValue(normalized)) return;
    const fingerprint = getAIGeneratedFingerprint(sections[section], section);
    if (nextSections[section] !== fingerprint) didChange = true;
    nextSections[section] = fingerprint;
  });

  if (!didChange && currentAI) {
    return { entity: source };
  }
  if (!didChange && Object.keys(nextSections).length === 0) {
    return { entity: source };
  }

  const aiGenerated = {
    v: AI_GENERATED_VERSION,
    at: now,
    sections: nextSections,
  };
  if (jobID) aiGenerated.jobID = jobID;
  else if (currentAI?.jobID) aiGenerated.jobID = currentAI.jobID;

  return { entity: { ...source, aiGenerated } };
};

const EMPTY_TEXT_LIST_FINGERPRINT = fnv1a32Hex("[]");

const mergeAIGeneratedSectionFingerprintUnsafe = ({
  entity,
  section,
  fingerprint,
  platform,
  entityType,
  now = Date.now(),
} = {}) => {
  if (
    !isAIGeneratedSectionForEntity({
      platform,
      entityType,
      section,
    })
  ) {
    return { entity, merged: false };
  }
  if (typeof fingerprint !== "string" || !/^[0-9a-f]{8}$/.test(fingerprint)) {
    return { entity, merged: false };
  }
  if (fingerprint === EMPTY_TEXT_LIST_FINGERPRINT) {
    return { entity, merged: false };
  }

  const source = entity && typeof entity === "object" ? entity : {};
  const normalized = normalizeAIGeneratedValue(
    getSectionValue(source, section),
    section
  );
  if (isEmptyAIGeneratedValue(normalized)) {
    return { entity: source, merged: false };
  }

  const currentAI =
    source.aiGenerated?.v === AI_GENERATED_VERSION ? source.aiGenerated : null;
  const previousSections = currentAI?.sections || {};
  if (previousSections[section] === fingerprint && currentAI) {
    return { entity: source, merged: false };
  }

  const aiGenerated = {
    v: AI_GENERATED_VERSION,
    at: currentAI?.at || now,
    sections: { ...previousSections, [section]: fingerprint },
  };
  if (currentAI?.jobID) aiGenerated.jobID = currentAI.jobID;
  return { entity: { ...source, aiGenerated }, merged: true };
};

export const mergeAIGeneratedSectionFingerprint = (params = {}) => {
  try {
    return mergeAIGeneratedSectionFingerprintUnsafe(params);
  } catch {
    reportFailure({
      functionName: "mergeAIGeneratedSectionFingerprint",
      section: params.section,
      entity: params.entity,
    });
    return { entity: params.entity, merged: false };
  }
};

export const stampAIGeneratedSections = (params = {}) => {
  try {
    return stampAIGeneratedSectionsUnsafe(params);
  } catch {
    const section = Object.keys(params.sections || [])[0];
    reportFailure({
      functionName: "stampAIGeneratedSections",
      section,
      entity: params.entity,
    });
    return { entity: params.entity };
  }
};

export const getCopiedAIGeneratedSectionHash = ({ sourceEntity, section }) => {
  if (!getIsAIGeneratedSection({ entity: sourceEntity, section })) return null;
  return sourceEntity.aiGenerated.sections[section];
};

const normalizeQuestion = (question) => {
  if (!question || typeof question !== "object") return null;
  const text = normalizeText(question.question || question.label || "");
  const options = Array.isArray(question.options)
    ? question.options
        .map((option) => {
          if (typeof option === "string") return normalizeText(option);
          return normalizeText(option?.text || option?.label || option?.value);
        })
        .filter(Boolean)
    : [];
  if (!text && options.length === 0) return null;
  return { options, question: text };
};

export const normalizeLeadFormDraft = (value) => {
  if (!value || typeof value !== "object") {
    return {
      contactFields: [],
      customQuestions: [],
      formType: "",
      introDescription: "",
      introHeadline: "",
      name: "",
      questionsHeadline: "",
    };
  }
  const contactFields = Array.isArray(value.contactFields)
    ? value.contactFields.map((item) => String(item)).sort()
    : [];
  const customQuestions = Array.isArray(value.customQuestions)
    ? value.customQuestions
        .map((question) => normalizeQuestion(question))
        .filter(Boolean)
    : [];
  return {
    contactFields,
    customQuestions,
    formType: normalizeText(value.formType),
    introDescription: normalizeText(value.introDescription),
    introHeadline: normalizeText(value.introHeadline),
    name: normalizeText(value.name),
    questionsHeadline: normalizeText(value.questionsHeadline),
  };
};

export const getLeadFormDraftFingerprint = (value) =>
  fnv1a32Hex(canonicalize(normalizeLeadFormDraft(value)));
